import { Add, Check, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, deDE, GridColDef } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useState } from 'react';
import { CreateLegalClientDialog } from '../../Clients/CreateLegalClientDialog';
import { useGetLegalClients } from '../../hooks/reactQuerys/LegalClientQueries';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { SearchType } from '../../Interfaces/ISearchType';
import { IPagination } from '../../Interfaces/IPagination';
import {
  getSearchUri,
  ISearchObject,
  legalClientsPossibleSearchOptions,
} from '../../Clients/ClientsSearch';
import { LoadingOverlay } from '../../Clients/ClientsOverview';

interface IProps {
  limit?: number;
  assignedLegalClients: ILegalClient[];
  setAssignedLegalClients: (newLegalClients: ILegalClient[]) => void;
  unmount: () => void;
  onlyJuristic?: boolean; //nur juristrische
}

const columns: GridColDef[] = [
  { field: 'idLegalClient', headerName: 'Nr.', flex: 1 },
  { field: 'ContactGroup', headerName: 'Gruppe', flex: 1 },
  {
    field: 'loginPossible',
    headerName: 'Login',
    flex: 1,
    renderCell: ({ value }) => (value ? <Check /> : <Close />),
  },
  {
    field: 'isReceivingEmailEnabled',
    headerName: 'E-Mails',
    flex: 1,
    renderCell: ({ value }) => (value ? <Check /> : <Close />),
  },
  { field: 'FirstName', headerName: 'Vorname', flex: 1 },
  { field: 'LastName', headerName: 'Nachname', flex: 1 },
  { field: 'Email', headerName: 'E-Mail', flex: 1 },
  { field: 'idArnoTopIntern', headerName: 'Namen.Nr.', flex: 1 },
];

export const AssignLegalClientsDialog: React.FC<IProps> = ({
  limit,
  assignedLegalClients,
  setAssignedLegalClients,
  unmount,
  onlyJuristic = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [rowSelectionModel, setRowSelectionModel] = useState<number[]>(
    assignedLegalClients.map(x => x.idLegalClient)
  );

  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchType, setSearchType] = useState(SearchType.INCLUDES);
  //
  const [showedData, setShowedData] = useState<IPagination<ILegalClient[]>>({
    count: 0,
    next: 1,
    previous: null,
    results: [],
  });
  //
  const [search, setSearch] = useState<ISearchObject>({
    searchTerm: '',
    searchFilter: legalClientsPossibleSearchOptions.map(
      item => item.SearchOptionKey
    ),
    groupFilter: [],
    lastLoginFilter: null,
    syncWithArnoTop: null,
    loginPossible: null,
    isReceivingEmailEnabled: null,
    searchType: 'includes',
    // Serverseitiger Filter: nur juristische Personen (idLegalClientType === 2)
    idLegalClientType: onlyJuristic ? 2 : null,
  });
  const [searchUri, setSearchUri] = useState(
    getSearchUri(page, pageSize, search)
  );
  const {
    legalClients,
    isLoadingLegalClients,
    refetchLegalClients,
    abortLegalClients,
  } = useGetLegalClients(searchUri);

  const selectionCount = rowSelectionModel.length;

  const isReachedLimit = useMemo(
    () => !!(limit && rowSelectionModel.length === limit),
    [limit, rowSelectionModel.length]
  );
  const [localCache, setLocalCache] = useState([...assignedLegalClients]);

  const onAssign = () => {
    const allKnownClients = [...localCache];

    const map = new Map(allKnownClients.map(c => [c.idLegalClient, c]));

    const newAssigned = rowSelectionModel
      .map(id => map.get(id))
      .filter(Boolean) as ILegalClient[];

    setAssignedLegalClients(newAssigned);
    unmount();
  };

  const onCreate = async (idLegalClient: number) => {
    setIsOpen(false);
    await refetchLegalClients();
    setRowSelectionModel(prev => [idLegalClient, ...prev]);
    setSearch(prev => ({
      ...prev,
      searchTerm: '',
    }));
  };

  const doSearch = (
    currentPage: number,
    currentPageSize: number,
    currentSearchObject: ISearchObject
  ) => {
    abortLegalClients();
    setSearchUri(
      getSearchUri(currentPage, currentPageSize, currentSearchObject)
    );
  };

  const debouncedSearch = useMemo(
    () =>
      debounce(
        (
          currentPage: number,
          currentPageSize: number,
          currenSearchObject: ISearchObject
        ) => {
          doSearch(currentPage, currentPageSize, currenSearchObject);
        },
        700
      ),
    []
  );

  useEffect(() => {
    setRowSelectionModel(assignedLegalClients.map(x => x.idLegalClient));
  }, [assignedLegalClients]);

  useEffect(() => {
    if (legalClients) {
      setShowedData(legalClients);
      setLocalCache(prev => [...prev, ...legalClients.results]);
    }
  }, [legalClients]);

  useEffect(() => {
    debouncedSearch(page, pageSize, search);
  }, [search]);

  return (
    <>
      <Dialog open onClose={unmount} maxWidth='lg' fullWidth>
        <>
          <DialogTitle>
            <Typography variant='h4'>Mandanten zuordnen</Typography>
            {limit && (
              <Typography variant='subtitle1' color='textSecondary'>
                {`Sie können maximal ${limit} Mandant/en auswählen.`}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '700px', // Gesamthöhe des Contents
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                size='small'
                label='Suche'
                value={search.searchTerm}
                fullWidth
                onChange={e =>
                  setSearch(prev => ({
                    ...prev,
                    searchTerm: e.target.value,
                  }))
                }
              />
              <IconButton
                disabled={isReachedLimit}
                onClick={() => setIsOpen(true)}
              >
                <Add />
              </IconButton>
            </Box>

            {/* Scrollbarer DataGrid-Bereich */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DataGrid
                sx={{ width: '100%', height: '100%' }}
                loading={isLoadingLegalClients}
                pagination
                paginationModel={{ page, pageSize }}
                rowCount={showedData.count}
                onPaginationModelChange={model => {
                  setPageSize(model.pageSize);
                  setPage(model.page);
                  debouncedSearch(model.page, model.pageSize, search);
                }}
                paginationMode='server'
                pageSizeOptions={[25, 50, 100]}
                slots={{ loadingOverlay: () => <LoadingOverlay /> }}
                checkboxSelection
                localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
                rows={showedData.results}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={newModel =>
                  setRowSelectionModel(newModel.map(Number))
                }
                keepNonExistentRowsSelected
                columns={columns}
                getRowId={({ idLegalClient }) => idLegalClient}
                isRowSelectable={({ row: { idLegalClient } }) =>
                  !isReachedLimit || rowSelectionModel.includes(idLegalClient)
                }
                initialState={{
                  pagination: { paginationModel: { pageSize: 15 } },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={unmount} variant='contained'>
              Abbrechen
            </Button>
            <Button
              variant='contained'
              onClick={onAssign}
              disabled={!selectionCount || isLoadingLegalClients}
            >
              Übernehmen
            </Button>
          </DialogActions>
        </>
      </Dialog>

      {isOpen && (
        <CreateLegalClientDialog
          onSave={onCreate}
          unmount={() => setIsOpen(false)}
          onlyJuristic={onlyJuristic}
        />
      )}
    </>
  );
};
